import type { BillingInterval, BillingTier } from "schemas/generated/billing";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";

const PlanCardButton = ({
  isCurrentTier,
  hasConfigurationChanges,
  isYearlyIntervalUpgrade,
  isDowngrade,
  currentPlan,
  handleSubmitPlanChanges,
  isManagedByReplo,
  isPlanConfigurationValid,
  invalidPlanConfigurationTooltipContent,
  billingInterval,
  isPlanCancelledButNotYetEnded,
}: {
  isCurrentTier: boolean;
  hasConfigurationChanges: boolean;
  isYearlyIntervalUpgrade: boolean;
  isDowngrade: boolean;
  currentPlan: BillingTier;
  handleSubmitPlanChanges: () => void;
  isManagedByReplo: boolean;
  isPlanConfigurationValid: boolean;
  invalidPlanConfigurationTooltipContent?: string;
  billingInterval: BillingInterval;
  isPlanCancelledButNotYetEnded: boolean;
}) => {
  const { buttonText, buttonVariant } = getButtonTextAndVariantForPlanCard(
    isCurrentTier,
    hasConfigurationChanges,
    isYearlyIntervalUpgrade,
    isDowngrade,
    currentPlan,
    billingInterval,
    isPlanCancelledButNotYetEnded,
  );

  const { isDisabled, tooltipContent } = (() => {
    const isCurrentPlanWithNoChanges =
      isCurrentTier && !hasConfigurationChanges;

    if (isManagedByReplo) {
      return {
        isDisabled: true,
        tooltipContent:
          "Your workspace's plan is managed by Replo. Please contact billing@replo.app to make changes.",
      };
    }

    if (!isPlanConfigurationValid) {
      return {
        isDisabled: true,
        tooltipContent: invalidPlanConfigurationTooltipContent,
      };
    }

    if (isCurrentPlanWithNoChanges && !isPlanCancelledButNotYetEnded) {
      return {
        isDisabled: true,
        tooltipContent: "This is your current plan",
      };
    }

    return {
      isDisabled: false,
      tooltipContent: undefined,
    };
  })();

  return (
    <Tooltip content={tooltipContent} triggerAsChild>
      {/* NOTE (Ben, 2026-02-09): Adding a span so tooltip is still triggered when button is disabled */}
      <span>
        <Button
          variant={buttonVariant}
          size="base"
          disabled={isDisabled}
          className="w-full"
          UNSAFE_className="mt-auto"
          onClick={handleSubmitPlanChanges}
        >
          {buttonText}
        </Button>
      </span>
    </Tooltip>
  );
};

export default PlanCardButton;

const getButtonTextAndVariantForPlanCard = (
  isCurrentTier: boolean,
  hasConfigurationChanges: boolean,
  isYearlyIntervalUpgrade: boolean,
  isDowngrade: boolean,
  currentPlan: BillingTier,
  billingInterval: BillingInterval,
  isPlanCancelledButNotYetEnded: boolean,
) => {
  let buttonText: string;
  let buttonVariant: "primary" | "secondary";

  if (isYearlyIntervalUpgrade) {
    buttonText = "Upgrade to Annual";
    buttonVariant = "primary";
  } else if (isCurrentTier && hasConfigurationChanges) {
    buttonText = "Update";
    buttonVariant = "primary";
  } else if (isCurrentTier && isPlanCancelledButNotYetEnded) {
    buttonText = "Resume Your Plan";
    buttonVariant = "primary";
  } else if (isCurrentTier) {
    buttonText = "Current Plan";
    buttonVariant = "secondary";
  } else if (isDowngrade) {
    buttonText = "Downgrade";
    buttonVariant = "secondary";
  } else if (currentPlan === "free") {
    buttonText =
      billingInterval === "yearly"
        ? "Choose Annual Plan"
        : "Choose Monthly Plan";
    buttonVariant = "primary";
  } else {
    buttonText = "Upgrade";
    buttonVariant = "primary";
  }

  return {
    buttonText,
    buttonVariant,
  };
};
